import { useEffect, useId, useState, type ReactElement } from 'react';
import * as Dialog from '@studio/design-system/dialog';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import type { DisplayedTrialNotice } from '#~/api/billing/trial-notice-controller.js';
import { useTrialNoticeShown } from './use-trial-notice-shown.js';

/** Duration comes from server authority plus elapsed monotonic time. */
export const trialNoticeRemainingTime = (seconds: number): string => {
  const remaining = Math.max(0, Math.floor(seconds));
  const days = Math.floor(remaining / 86400);
  const hours = Math.floor((remaining % 86400) / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  return parts.length ? parts.join(', ') : 'less than a minute';
};

/** Studio-member warning; every role has the same informational Close action. */
export const StandardTrialWarningNotice = ({
  roomId,
  view,
  onShown,
  onDismiss,
  onElapsed
}: {
  readonly roomId: string;
  readonly view: DisplayedTrialNotice;
  readonly onShown: () => void;
  readonly onDismiss: () => void;
  readonly onElapsed: () => void;
}): ReactElement => {
  const descriptionId = useId();
  const { ref, reportVisible } = useTrialNoticeShown(onShown);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const update = () => {
      const seconds = Math.max(0, (performance.now() - view.receivedAt) / 1000);
      setElapsed(seconds);
      if (seconds >= view.notice.remainingSeconds) onElapsed();
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [view.receivedAt, view.notice.remainingSeconds, onElapsed]);
  const remaining = trialNoticeRemainingTime(view.notice.remainingSeconds - elapsed);
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) {
          reportVisible();
          onDismiss();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          ref={ref}
          data-trial-notice
          closeOnEscape
          aria-describedby={descriptionId}
          className='border-base-400 fixed left-1/2 top-1/2 max-h-[calc(100dvh_-_32px)] w-[calc(100vw_-_32px)] max-w-[480px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-12 border p-24 shadow-lg'
          {...DataSTag(Sel.StandardTrialNotice, 'reminder', roomId)}
        >
          <div className='flex items-start gap-8'>
            <Dialog.Title className='grow'>
              Your {view.notice.offerName} trial expires in {remaining}
            </Dialog.Title>
            <Dialog.Dismiss
              aria-label='Close'
              className='focus-visible:ring-primary/50 focus-visible:ring-2'
              {...STagNew(Sel.StandardTrialNotice, 'dismiss-warning', roomId)}
            />
          </div>
          <p id={descriptionId} className='mt-8 break-words text-sm text-muted'>
            {view.notice.reminderMessage}
          </p>
          <Dialog.Close
            variant='secondary'
            className='focus-visible:ring-primary/50 mt-20 w-full focus-visible:ring-2'
            {...STagNew(Sel.StandardTrialNotice, 'close-warning', roomId)}
          >
            Close
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
